import { useEffect, useMemo, useState } from 'react'
import { Alert, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { shortId } from '@/shared/lib/shortId'
import { LedgerOwnerName } from '@/features/ledger/LedgerOwnerName'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { TableSkeleton } from '@/shared/ui/table/TableSkeleton'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { JournalFilterBar } from '@/features/ledger/JournalFilterBar'
import { JournalOwnerBanner } from '@/features/ledger/JournalOwnerBanner'
import { JournalScopeBanner } from '@/features/ledger/JournalScopeBanner'
import { LedgerTxnModal } from '@/features/ledger/LedgerTxnModal'
import {
  JOURNAL_SCOPE_ALL,
  fetchJournalRows,
  formatJournalAmount,
  hiddenCounterparties,
  journalDayRange,
  journalScope,
  journalSortable,
  normalizeJournalParams,
  type JournalRow,
} from '@/features/ledger/ledgerJournalApi'

function firstFilter(filters: Readonly<Record<string, readonly string[]>>, key: string): string | null {
  const values = filters[key]
  return values && values.length > 0 ? (values[0] ?? null) : null
}

/** Ega turkumi + qisqartirilgan id — jurnalda login yo'q, faqat polimorfik havola */
function OwnerCell({ type, id }: { type: string | null; id: string | null }) {
  const { t } = useTranslation()
  if (type === null && id === null) return <>{t('common.dash')}</>
  if (type !== null && id !== null) return <LedgerOwnerName type={type} id={id} />
  return (
    <Space size={4} wrap>
      {type === null ? null : <Tag>{t(`ledgerOwnerType.${type}`, { defaultValue: type })}</Tag>}
      {id === null ? null : (
        <Typography.Text code copyable={{ text: id }} style={{ fontSize: 12 }}>
          {shortId(id)}
        </Typography.Text>
      )}
    </Space>
  )
}

/**
 * TRANZAKSIYALAR — butun pul jurnali bitta filtrlanadigan jadvalda.
 *
 * <h2>⚠️ QATOR = YOZUV, TRANZAKSIYA EMAS</h2>
 * Bitta amal jurnalda kamida ikki yozuv qoldiradi: depozit `+300 000`,
 * sotuv `−30 000`, komissiya `+3 000` — uchtasi ALOHIDA qator va ularni
 * `txnId` bog'laydi (`docs/02-LEDGER.md` §3). Panel buni YASHIRMAYDI:
 * yig'ib ko'rsatish "pul qayerdan keldi" degan savolni javobsiz
 * qoldirardi, holbuki jurnalning butun ma'nosi shunda.
 *
 * <h2>⚠️ NEGA BU SAHIFA `/ledger` DAN ALOHIDA VA UNI ALMASHTIRMAYDI</h2>
 * `LedgerPage` (`/ledger`) — HISOB markazli: hisoblar ro'yxati, qoldiq,
 * qarz limiti, muvofiqlashtirish hisoboti, agent balanslari va qo'lda
 * tuzatish. `GET /api/ledger/entries` bularning BIRORTASINI ham
 * bermaydi. Uni bu sahifa bilan almashtirish beshta funksiyani JIMGINA
 * yo'q qilardi. `FinanceJournalPage` (`/finance/journal`) esa umuman
 * boshqa modul (`xsib-finance`): buxgalteriya provodkalari, davrlar va
 * xarajat kiritish. Uchalasi bir mavzuda, lekin uch xil savolga javob
 * beradi.
 *
 * <h2>⚠️ «JAMI N» YOZILMAYDI</h2>
 * Javobda `total` YO'Q, faqat `hasNext` (`LedgerPage` rekordi). Taxminiy
 * sonni "jami" deb ko'rsatish operator ishonadigan yolg'on bo'lardi.
 *
 * <h2>⚠️ AVTOYANGILANISH YO'Q — egasi qarori</h2>
 * Faqat bosh sahifada. Pul jurnali o'zi ostidan siljib turmasin.
 *
 * <h2>⚠️ ADR-033 (yopilganlar) BU YERGA QO'LLANMAYDI</h2>
 * Jurnal yozuvining HOLATI yo'q — u o'zgarmas fakt. Xatosi teskari yozuv
 * bilan tuzatiladi (`REVERSAL`), o'chirilmaydi. Bu yerda biror qatorni
 * sukut bo'yicha yashirish yig'indini buzardi, ya'ni ADR-033 dagi
 * "moliya davri" istisnosi bilan bir xil holat.
 */
export function TransactionsPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { state, patch } = useTableUrlState()
  const [openTxnId, setOpenTxnId] = useState<string | null>(null)

  /*
    ⚠️ Abonent kartasidan kelgan havola prefikssiz parametr bilan keladi
    (`?ownerType=SUBSCRIBER&ownerId=…`), jadval holati esa `f.` bilan
    yashaydi. Ikkalasi yonma-yon qolsa «Tozalash» ro'yxatni tozalamasdi —
    sabab manzilda ko'rinmay qolardi (`normalizeJournalParams` javadoc'i).
  */
  const pending = useMemo(() => normalizeJournalParams(searchParams), [searchParams])
  useEffect(() => {
    if (pending) setSearchParams(pending, { replace: true })
  }, [pending, setSearchParams])

  const ownerType = firstFilter(state.filters, 'ownerType')
  const ownerId = firstFilter(state.filters, 'ownerId')
  const range = journalDayRange(state.filters)
  const scope = journalScope(state.filters)

  const columns = useMemo<ColumnsType<JournalRow>>(
    () => [
      {
        key: 'createdAt',
        title: t('ledgerJournal.columns.at'),
        width: 170,
        sorter: journalSortable('createdAt'),
        render: (_, row) => (
          <span title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</span>
        ),
      },
      {
        key: 'txnKind',
        title: t('ledgerJournal.columns.kind'),
        width: 200,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            {row.txnKind === null ? (
              <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
            ) : (
              <Tag>{t(`txnKind.${row.txnKind}`, { defaultValue: row.txnKind })}</Tag>
            )}
            {row.txnReference === null ? null : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.txnReference}
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        key: 'account',
        title: t('ledgerJournal.columns.account'),
        width: 250,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            {row.accountType === null ? null : (
              <Typography.Text>
                {t(`ledgerAccountType.${row.accountType}`, { defaultValue: row.accountType })}
              </Typography.Text>
            )}
            <OwnerCell type={row.ownerType} id={row.ownerId} />
          </Space>
        ),
      },
      {
        key: 'amount',
        title: t('ledgerJournal.columns.amount'),
        align: 'right',
        width: 190,
        sorter: journalSortable('amount'),
        render: (_, row) => (
          /*
            ⚠️ Ishora KELISHUVI `ledger` niki: musbat = kredit, manfiy =
            debit (`docs/02-LEDGER.md` §7). Panel uni AG'DARMAYDI.
          */
          <Typography.Text
            type={row.amount < 0 ? 'danger' : 'success'}
            style={{ whiteSpace: 'nowrap' }}
          >
            {row.amount >= 0 ? '+' : ''}
            {/* ⚠️ `money` bayrog'i — `BYTE` ni pul kabi formatlash MING
                BAROBAR yolg'on beradi (`formatJournalAmount`) */}
            {formatJournalAmount(row)}
          </Typography.Text>
        ),
      },
      {
        key: 'counter',
        title: t('ledgerJournal.columns.counter'),
        width: 260,
        render: (_, row) => {
          const hidden = hiddenCounterparties(row)
          if (row.counterAccountType === null && row.counterOwnerId === null) {
            return (
              <Tooltip title={t('ledgerJournal.counterNoneHint')}>
                <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
              </Tooltip>
            )
          }
          return (
            <Space direction="vertical" size={0}>
              {row.counterAccountType === null ? null : (
                <Typography.Text>
                  {t(`ledgerAccountType.${row.counterAccountType}`, {
                    defaultValue: row.counterAccountType,
                  })}
                </Typography.Text>
              )}
              <OwnerCell type={row.counterOwnerType} id={row.counterOwnerId} />
              {/*
                ⚠️ «va yana N ta» — YASHIRILGAN QARSHI TOMONLAR. Server
                `counterCount > 1` bo'lganda faqat ENG KATTASINI beradi
                (`JournalRow` javadoc'i) va buni aytmaslik ekranni
                yolg'onchi qilardi: operator ikki tomonlama yozuvni
                to'liq ko'rdim deb o'ylardi.
              */}
              {hidden > 0 ? (
                <Tooltip title={t('ledgerJournal.counterMoreHint')}>
                  <Typography.Text type="warning" style={{ fontSize: 12 }}>
                    {t('ledgerJournal.counterMore', { count: hidden })}
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </Space>
          )
        },
      },
      {
        key: 'txnNote',
        title: t('ledgerJournal.columns.note'),
        render: (_, row) =>
          row.txnNote === null ? (
            <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
          ) : (
            row.txnNote
          ),
      },
      {
        key: 'txnId',
        title: t('ledgerJournal.columns.txnId'),
        width: 120,
        render: (_, row) => (
          <Tooltip title={t('ledgerJournal.txnIdHint')}>
            <Typography.Text code style={{ fontSize: 12 }}>
              {row.txnId}
            </Typography.Text>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'txn',
                label: t('ledger.txn.open'),
                onClick: () => setOpenTxnId(row.txnId),
              },
              {
                key: 'owner',
                label: t('ledgerJournal.filterByOwner'),
                hidden: row.ownerType === null || row.ownerId === null,
                onClick: () =>
                  patch({
                    page: 1,
                    filters: {
                      ...state.filters,
                      ownerType: row.ownerType === null ? [] : [row.ownerType],
                      ownerId: row.ownerId === null ? [] : [row.ownerId],
                    },
                  }),
                divider: true,
              },
            ]}
          />
        ),
      },
    ],
    [t, patch, state.filters],
  )

  return (
    <>
      <PageHeader title={t('nav.transactions')} description={t('ledgerJournal.intro')} />

      {/*
        ⚠️ «Qator = yozuv» EKRANDA aytiladi. Busiz operator bitta to'lovni
        ro'yxatda uch marta ko'rib, "takrorlanib ketibdi" deb xabar
        qilardi — 2026-08 gacha aynan shunday savollar kelgan.
      */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('ledgerJournal.entryRuleTitle')}
        description={t('ledgerJournal.entryRuleBody')}
      />

      {/*
        ⚠️ SUKUT TORAYISHI EKRANDA. Ro'yxat faqat pul yozuvlarini
        so'raydi, jurnalda esa trafik yozuvlari ko'p — busiz operator
        ularni «yo'q» deb o'qirdi.
      */}
      <JournalScopeBanner
        scope={scope}
        onShowAll={() =>
          patch({ page: 1, filters: { ...state.filters, scope: [JOURNAL_SCOPE_ALL] } })
        }
        onMoneyOnly={() => patch({ page: 1, filters: { ...state.filters, scope: [] } })}
        onClearUnit={() => patch({ page: 1, filters: { ...state.filters, unit: [] } })}
      />

      {ownerType !== null && ownerId !== null ? (
        <JournalOwnerBanner
          ownerType={ownerType}
          ownerId={ownerId}
          onClear={() => patch({ page: 1, filters: { ...state.filters, ownerType: [], ownerId: [] } })}
        />
      ) : null}

      {pending ? (
        // Havola eski shaklda kelgan — bir renderlik ko'chirish. Jadvalni
        // shu paytda chizish FILTRSIZ so'rov yuborardi.
        <TableSkeleton />
      ) : (
        <DataTable<JournalRow>
          // ⚠️ Davr KALITDA emas: u `state.filters` ichida va `DataTable`
          // jadval holatini kalitga o'zi qo'shadi (`toQueryParams`).
          queryKey={queryKeys.ledger.journal}
          fetchPage={fetchJournalRows}
          columns={columns}
          rowKey={(row) => row.id}
          tableId="ledger-journal"
          stickyFirstColumn
          scrollX={1500}
          searchable
          searchPlaceholder={t('ledgerJournal.searchPlaceholder')}
          // ⚠️ Server JAMI SONNI BERMAYDI — javobda faqat `hasNext`
          showTotal={false}
          toolbarExtra={<JournalFilterBar />}
          exportColumns={[
            { header: t('ledgerJournal.columns.at'), value: (row) => row.createdAt },
            { header: t('ledgerJournal.columns.txnId'), value: (row) => row.txnId },
            { header: t('ledgerJournal.columns.kind'), value: (row) => row.txnKind ?? '' },
            { header: t('ledgerJournal.columns.account'), value: (row) => row.accountType ?? '' },
            { header: t('ledgerJournal.filters.ownerType'), value: (row) => row.ownerType ?? '' },
            { header: t('ledgerJournal.filters.ownerId'), value: (row) => row.ownerId ?? '' },
            // ⚠️ CSV'ga XOM MINOR son ketadi — u yerda birlik ALOHIDA
            // ustunda va aylantirish `unit` ni bilmasdan qilinsa
            // trafikni 100 ga bo'lib yuborardi
            { header: t('ledgerJournal.columns.amount'), value: (row) => row.amount },
            { header: t('ledgerJournal.filters.unit'), value: (row) => row.unit },
            { header: t('ledgerJournal.columns.counter'), value: (row) => row.counterAccountType ?? '' },
            { header: t('ledgerJournal.counterCountHeader'), value: (row) => row.counterCount },
            { header: t('ledgerJournal.columns.note'), value: (row) => row.txnNote ?? '' },
          ]}
          exportFilename={`transactions-${range.from}_${range.to}`}
          emptyTitle={t('ledgerJournal.empty')}
          // ⚠️ Bo'sh ro'yxatda sabab IKKI xil bo'lishi mumkin: davr yoki
          // «faqat pul» sukuti. Ikkinchisini aytmaslik trafik yozuvlarini
          // «yo'q» qilib ko'rsatardi.
          emptyDescription={
            scope.moneyOnly ? t('ledgerJournal.emptyHintMoney') : t('ledgerJournal.emptyHint')
          }
          onRow={(row) => ({
            style: { cursor: 'pointer' },
            onClick: () => setOpenTxnId(row.txnId),
          })}
        />
      )}

      <LedgerTxnModal txnId={openTxnId} onClose={() => setOpenTxnId(null)} />
    </>
  )
}
